import * as FluxUtils from 'flux/utils';
import { AppDispatcher, DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { AppConfigurationStore } from './AppConfigurationStore';
import { GlobalHelper } from '../utils/GlobalHelper';
import { LayoutType } from '../constants/LayoutConstants';
import { SidebarModule, HamburgerTab, MessageBoxType } from '../constants/Enums';
import { PersistentStore } from './PersistentStore';

export interface MessageBoxPayload {
    wrap: boolean;
    element: JSX.Element;
    type: MessageBoxType;
}

export enum LockState {
    UNLOCKED,
    ANIMATION,
    COMPONENT
}

interface ViewSettingsState {
    activeSubChoice: string | null;
    alllocked: LockState;
    alllockedComponent: JSX.Element | null;
    alllockedTransparency: boolean;
    bannerHidden: boolean;
    checkboxstates: { [key: string]: number };
    extraButtons: boolean;
    hideBars: boolean;
    layoutType: LayoutType;
    messagePayload: MessageBoxPayload | null;
    networkError: boolean;
    hamburgerTab: HamburgerTab | null;
    sharetab: string | null;
    sidebar: SidebarModule;
    sidebarlocked: boolean;
    subchoicestates: { [key: string]: number };
    dragMode: boolean;
}

class ViewSettingsStoreClass extends FluxUtils.ReduceStore<ViewSettingsState, unknown> {
    public getBrowser(): string {
        if (AppConfigurationStore.getState().inApp) {
            return 'noBrowser';
        }
        if (navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1) {
            return 'Safari';
        }
        if (navigator.userAgent.indexOf('Chrome') !== -1) {
            return 'Chrome';
        }
        return 'else';
    }

    public showProjectButton(): boolean {
        const state: ViewSettingsState = this.getState();
        return (
            state.sidebar !== SidebarModule.PROJECTS &&
            AppConfigurationStore.getState().projectManagerAvailable
        );
    }

    public getInitialState(): ViewSettingsState {
        return {
            activeSubChoice: null,
            alllocked: LockState.UNLOCKED,
            alllockedComponent: null,
            alllockedTransparency: false,
            bannerHidden: false,
            checkboxstates: {},
            extraButtons: false,
            hideBars: false,
            layoutType: this.getLayoutType(true),
            messagePayload: null,
            networkError: false,
            hamburgerTab: null,
            sharetab: null,
            sidebar: PersistentStore.shouldStartWithProjectManagement()
                ? SidebarModule.PROJECTS :
                !AppConfigurationStore.getState().inApp
                    ? SidebarModule.CATALOG
                    : SidebarModule.NONE,
            sidebarlocked: false,
            subchoicestates: {},
            dragMode: false
        };
    }

    public reduce(state: ViewSettingsState, action: DispatcherBaseAction): ViewSettingsState {
        // Note: this function should be `pure` -> we have to return a copy
        // of the current state if some property was changed

        switch (action.actionType) {
            case ActionConstants.HIDE_BANNER:
                return jQuery.extend({}, state, {bannerHidden: true});
            case ActionConstants.SHOW_SELECTBOX:
                if (
                    action.value != null &&
                    action.value.asOverlay === false &&
                    action.value.options != null &&
                    action.value.options.length > 0
                ) {
                    return jQuery.extend({}, state, {
                        hamburgerTab: HamburgerTab.SETTINGS
                    });
                }
                break;
            case ActionConstants.MAXIMIZE_SCENE:
                if (!action.value) {
                    return jQuery.extend({}, state, {
                        hideBars: false
                    });
                }
                break;
            case ActionConstants.TOGGLE_BARS:
                return jQuery.extend({}, state, {
                    hideBars: !action.value
                });
            case ActionConstants.WINDOW_RESIZE:
                return jQuery.extend({}, state, {
                    layoutType: this.getLayoutType()
                });
            case ActionConstants.UPDATE_SELECTION:
                if (
                    (!action.value.elements || !action.value.elements.length) &&
                    (
                        state.sidebar === SidebarModule.PROPERTIES ||
                        state.sidebar === SidebarModule.INSPIRATIONS
                    )
                ) {
                    return jQuery.extend({}, state, {
                        sidebar: SidebarModule.NONE
                    });
                }
                break;
            case ActionConstants.SHOW_EXTRABUTTONS:
                return jQuery.extend({}, state, {
                    extraButtons: action.value
                });
            case ActionConstants.ARTICLE_LIST_CHANGED:
                if (
                    GlobalHelper.coreApp.model.elements.length === 0 &&
                    (
                        state.sidebar === SidebarModule.SHARE ||
                        state.sidebar === SidebarModule.ARTICLELIST ||
                        state.sidebar === SidebarModule.PROPERTIES ||
                        state.sidebar === SidebarModule.INSPIRATIONS
                    )
                ) {
                    return jQuery.extend({}, state, {
                        sidebar: SidebarModule.NONE
                    });
                }
                break;
            case ActionConstants.ARTICLE_ADDED:
                return jQuery.extend({}, state, {
                    sidebar: SidebarModule.NONE,
                    alllocked: LockState.UNLOCKED,
                    dragMode: false
                });
            case ActionConstants.CHANGE_SIDEBAR:
                return {...state, sidebar: action.value};
            case ActionConstants.CHANGE_HAMBURGER:
                return {...state, hamburgerTab: action.value};
            case ActionConstants.CHANGE_SHARETAB:
                return jQuery.extend({}, state, {
                    sharetab: action.value
                });
            case ActionConstants.LOCK_SIDEBAR:
                return jQuery.extend({}, state, {
                    sidebarlocked: action.value
                });
            case ActionConstants.LOCK_ALL:
                return jQuery.extend({}, state, {
                    alllocked: action.value.state,
                    alllockedComponent: action.value.component,
                    alllockedTransparency: action.value.transparent
                });
            case ActionConstants.TOGGLE_CHECKBOX:
                const tGroup: string = action.value.group;
                const tId: number = action.value.id;
                const tCurrentState: {[key: string]: number} = state.checkboxstates ? state.checkboxstates : {};
                const tNewState: ViewSettingsState = jQuery.extend({}, tCurrentState);
                tNewState[tGroup] = tId;
                return jQuery.extend({}, state, {
                    checkboxstates: tNewState
                });
            case ActionConstants.TOGGLE_CHOICEMENU:
                return jQuery.extend({}, state, {
                    activeSubChoice: action.value
                });
            case ActionConstants.TOGGLE_SUBCHOICE:
                const tGroupSC: string = action.value.group;
                const tIdSC: number = action.value.id;
                const tCurrentStateSC: { [key: string]: number} = state.subchoicestates ? state.subchoicestates : {};
                const tNewStateSC: ViewSettingsState = jQuery.extend({}, tCurrentStateSC);
                tNewStateSC[tGroupSC] = tIdSC;
                return jQuery.extend({}, state, {
                    subchoicestates: tNewStateSC, activeSubChoice: null
                });
            case ActionConstants.SET_MESSAGE:
                return jQuery.extend({}, state, {
                    messagePayload: action.value
                });
            case ActionConstants.SET_NETWORK_ERROR:
                return jQuery.extend({}, state, {
                    networkError: action.value
                });
            case ActionConstants.TRIGGER_DOWNLOAD:
                return jQuery.extend({}, state, {
                    messagePayload: action.value,
                    alllocked: LockState.UNLOCKED
                });
            case ActionConstants.SET_DRAG_MODE:
                return jQuery.extend({}, state, {
                    dragMode: action.value
                });
            default:
                break;
        }

        return state;
    }

    protected getLayoutType(skipToolSetup: boolean = false): LayoutType {
        const height: number = window.innerHeight || document.documentElement.clientHeight || 0;
        const width: number = window.innerWidth || document.documentElement.clientWidth || 0;
        if (width <= 921 && height <= 921) {
            if (!skipToolSetup) {
                GlobalHelper.updateDefaultTool(AppConfigurationStore.getState().inApp);
            }
            return LayoutType.SMALL;
        }
        if (!skipToolSetup) {
            GlobalHelper.updateDefaultTool(false);
        }
        if (width < 1025) {
            return LayoutType.TABLET;
        }
        return LayoutType.DESKTOP;
    }
}

export let ViewSettingsStore: ViewSettingsStoreClass = new ViewSettingsStoreClass(AppDispatcher);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/ViewSettingsStore.ts